import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Switch,
  IconButton,
  CircularProgress,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { FilterRule, RuleCondition } from '../../types';
import apiClient, {
  createInvestmentThesisFilter,
  updateInvestmentThesisFilter,
  deleteInvestmentThesisFilter,
} from '../../services/api';

interface RulesEngineTabProps {
  thesisId: string | null;
  isPrivilegedUser: boolean;
  disableAddRule: boolean;
}

export const RulesEngineTab: React.FC<RulesEngineTabProps> = ({ thesisId, isPrivilegedUser, disableAddRule }) => {
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState<FilterRule | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleType, setRuleType] = useState('highlight');
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [conditions, setConditions] = useState<RuleCondition[]>([]);

  const fieldConfig: Record<string, { type: 'string' | 'numeric'; operators: RuleCondition['operator'][] }> = {
    'Deal Size': { type: 'numeric', operators: ['equals', 'not_equals', 'greater_than', 'less_than'] },
    'Industry': { type: 'string', operators: ['equals', 'not_equals', 'contains'] },
    'YoY Growth': { type: 'numeric', operators: ['equals', 'not_equals', 'greater_than', 'less_than'] },
    'EBITDA Margin': { type: 'numeric', operators: ['equals', 'not_equals', 'greater_than', 'less_than'] },
    'Geography': { type: 'string', operators: ['equals', 'not_equals', 'contains'] },
  };

  const fetchRules = useCallback(async () => {
    if (!thesisId) return;
    setLoadingRules(true);
    try {
      const response = await apiClient.get(`/api/v1/investment-thesis/${thesisId}/filters`);
      setRules(response.data || []);
    } catch (error) {
      console.error("Failed to fetch rules", error);
      setRules([]);
    } finally {
      setLoadingRules(false);
    }
  }, [thesisId]);

  useEffect(() => {
    fetchRules();
  }, [thesisId, fetchRules]);

  const handleSaveRule = async () => {
    if (!ruleName?.trim()) {
      window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'Please enter a rule name' }));
      return;
    }

    if (!thesisId) {
      window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'Thesis ID is missing' }));
      return;
    }

    try {
      setSaving(true);
      const ruleData = {
        name: ruleName,
        description: ruleDescription,
        filter_type: ruleType,
        thesis_id: thesisId,
        conditions: conditions,
        is_active: editingRule?.is_active === false ? false : true,
        priority: 0
      };

      if (editingRule) {
        await updateInvestmentThesisFilter(editingRule.id, ruleData);
        const event = new CustomEvent('apiSuccess', { detail: 'Rule updated successfully!' });
        window.dispatchEvent(event);
      } else {
        await createInvestmentThesisFilter(thesisId, ruleData);
        const event = new CustomEvent('apiSuccess', { detail: 'Rule created successfully!' });
        window.dispatchEvent(event);
      }
      
      fetchRules();
      setEditingRule(null);
      setRuleName('');
      setRuleDescription('');
      setRuleType('highlight');
      setConditions([]);
      setView('list'); // Switch back to the list view
    } catch (err: any) {
      console.error('Error saving rule:', err);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to save rule: ' + (err.response?.data?.detail || err.message) }));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule: FilterRule) => {
    setEditingRule(rule);
    setRuleName(rule.name);
    setRuleDescription(rule.description);
    setRuleType(rule.filter_type);
    setConditions(rule.conditions || []);
    setView('form'); // Switch to the form view for editing
  };

  const handleAddCondition = () => {
    setConditions([...conditions, { id: '', field: '', operator: 'equals', value: '' }]);
  };

  const handleConditionChange = (index: number, field: keyof RuleCondition, value: any) => {
    const newConditions = [...conditions];
    const oldCondition = newConditions[index];
    newConditions[index] = { ...newConditions[index], [field]: value };

    if (field === 'field') {
      const config = fieldConfig[value as keyof typeof fieldConfig];
      if (config && !config.operators.includes(oldCondition.operator)) {
        newConditions[index].operator = config.operators[0];
      }
      newConditions[index].value = ''; // Clear value when field changes
    }

    setConditions(newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
  };

  const handleToggle = async (rule: FilterRule) => {
    try {
      await updateInvestmentThesisFilter(rule.id, { ...rule, is_active: !rule.is_active });
      fetchRules();
      const event = new CustomEvent('apiSuccess', { detail: 'Rule updated successfully!' });
      window.dispatchEvent(event);
    } catch (error) {
      console.error("Failed to update rule", error);
    }
  };

  const handleDelete = async (ruleId: string) => {
    try {
      await deleteInvestmentThesisFilter(ruleId);
      fetchRules();
      const event = new CustomEvent('apiSuccess', { detail: 'Rule deleted successfully!' });
      window.dispatchEvent(event);
    } catch (error) {
      console.error("Failed to delete rule", error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>⚙️ Filters & Rules Engine</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Define automated triage logic to streamline deal processing and highlight opportunities.
      </Typography>

      {view === 'list' ? (
        // View for listing rules
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {loadingRules ? (
              <CircularProgress />
            ) : !thesisId ? (
              <Alert severity="warning">Please save the thesis to create rules.</Alert>
            ) : (
              rules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{rule.name}</Typography>
                        <Chip
                          label={rule.filter_type}
                          size="small"
                          color={
                            rule.filter_type === 'Auto-Accept' ? 'success' :
                            rule.filter_type === 'Auto-Reject' ? 'error' :
                            'primary'
                          }
                          variant="outlined"
                        />
                        <Switch
                          checked={rule.is_active}
                          onChange={() => handleToggle(rule)}
                          size="small"
                          disabled={!isPrivilegedUser}
                        />
                      </Box>
                      {isPrivilegedUser && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" onClick={() => handleEdit(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(rule.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Conditions:</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {rule.conditions.map((condition: any, index: number) => (
                          <Box key={condition.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {index > 0 && condition.logicalOperator && (
                              <Chip
                                label={condition.logicalOperator}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 1 }}
                              />
                            )}
                            <Chip label={condition.field} size="small" />
                            <Chip label={condition.operator.replace('_', ' ')} size="small" variant="outlined" />
                            <Chip label={condition.value} size="small" color="primary" />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Action:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rule.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
            {isPrivilegedUser && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  setEditingRule(null);
                  setRuleName('');
                  setRuleDescription('');
                  setRuleType('highlight');
                  setConditions([]);
                  setView('form');
                }}
                disabled={disableAddRule}
              >
                Create New Rule
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        // View for creating/editing a rule
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Rule Name"
                  placeholder="Enter rule name"
                  size="small"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                />
                <FormControl fullWidth size="small">
                  <Typography variant="body2" gutterBottom>Rule Type</Typography>
                  <Select value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
                    <MenuItem value="Auto-Accept">Auto-Accept</MenuItem>
                    <MenuItem value="Auto-Reject">Auto-Reject</MenuItem>
                    <MenuItem value="Highlight">Highlight</MenuItem>
                  </Select>
                </FormControl>
                <Divider />
                <Typography variant="subtitle2">Conditions</Typography>
                {conditions.map((condition, index) => {
                  const fieldType = fieldConfig[condition.field as keyof typeof fieldConfig]?.type;
                  const operators = fieldConfig[condition.field as keyof typeof fieldConfig]?.operators || [
                    'equals',
                    'not_equals',
                    'greater_than',
                    'less_than',
                    'contains',
                  ];

                  return (
                    <Box key={index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={condition.field}
                            onChange={(e) => handleConditionChange(index, 'field', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Select field</MenuItem>
                            <MenuItem value="Deal Size">Deal Size</MenuItem>
                            <MenuItem value="Industry">Industry</MenuItem>
                            <MenuItem value="YoY Growth">YoY Growth</MenuItem>
                            <MenuItem value="EBITDA Margin">EBITDA Margin</MenuItem>
                            <MenuItem value="Geography">Geography</MenuItem>
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <FormControl size="small" sx={{ flex: 1 }}>
                            <Select
                              value={condition.operator}
                              onChange={(e) => handleConditionChange(index, 'operator', e.target.value)}
                              displayEmpty
                            >
                              {operators.map((op) => (
                                <MenuItem key={op} value={op}>
                                  {op.replace(/_/g, ' ')}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            placeholder="Value"
                            size="small"
                            sx={{ flex: 1 }}
                            value={condition.value}
                            type={fieldType === 'numeric' ? 'number' : 'text'}
                            onChange={(e) => {
                              const { value } = e.target;
                              if (fieldType === 'numeric') {
                                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                  handleConditionChange(index, 'value', value);
                                }
                              } else {
                                handleConditionChange(index, 'value', value);
                              }
                            }}
                          />
                        </Box>
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveCondition(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddCondition}>
                  Add Condition
                </Button>
                <Divider />
                <Typography variant="subtitle2">Action</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe what happens when conditions are met"
                  size="small"
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button variant="contained" size="small" fullWidth onClick={handleSaveRule} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Rule'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setEditingRule(null);
                      setRuleName('');
                      setRuleDescription('');
                      setRuleType('highlight');
                      setView('list');
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Rules Engine</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter sample deal data to test how your rules would process it.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <TextField
              label="Deal Size (USD)"
              type="number"
              defaultValue="25000000"
              sx={{ flex: 1, minWidth: '200px' }}
            />
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <FormControl fullWidth>
                <Typography variant="body2" gutterBottom>Industry</Typography>
                <Select defaultValue="Technology">
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                  <MenuItem value="Financial Services">Financial Services</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="YoY Growth (%)"
              type="number"
              defaultValue="35"
              sx={{ flex: 1, minWidth: '200px' }}
            />
            <TextField
              label="EBITDA Margin (%)"
              type="number"
              defaultValue="28"
              sx={{ flex: 1, minWidth: '200px' }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Test Results:</Typography>
            <Alert severity="success" sx={{ mb: 1 }}>
              <strong>Auto-Accept:</strong> Strategic Sectors rule triggered - Fast-track to preliminary due diligence
            </Alert>
            <Alert severity="info">
              <strong>Highlight:</strong> High Growth rule triggered - Highlight in green and flag for VP review
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button variant="contained">Run Test</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};